import { useCallback, useMemo, useState } from 'react';
import { InlineNotification, Modal } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { formatNumber } from 'utils/number';
import { useLoadableAtomValue } from 'lib/hooks/useLoadableAtomValue';
import { FullNRITable } from 'components/nri-table/nri-table';
import { ServerExportParams } from 'components/table/table-actions';
import { packageSlotSummaryAtom } from './atoms';
import { useEvalExportPresets } from '../eval-3-workspace/hooks';
import { afeChangedSinceLastRunAtom } from '../eval-3-workspace/atoms';
import { AFE_COLUMNS, ENHANCED_OVERVIEW_SLOT_COLUMNS } from './utils';
import {
  useEnhancedSlotAllocationsPaginated,
  useSlotsExport,
} from './aggregated-free-cash-flow/hooks';
import { SlotsSummary, SlotSummaryRow } from 'components/slot-summary-table';
import { SlotVolumeSource, SlotAfeSource } from 'types/evaluation';
import {
  SlotVolumeSourceConfiguration,
  SlotAfeSourceConfiguration,
  SlotCostDateConfiguration,
} from './aggregated-free-cash-flow/types';
import { CostDateField, SlotDateField } from 'components/nri-table/types';
import type { BulkEditSavePayload } from 'components/nri-table/bulk-edit-slots';
import type {
  ScheduledSlotDates,
  SlotDateOverride,
} from '../eval-3-workspace/types';
import { EditVolumeSourceModal } from './edit-volume-source-modal/edit-volume-source-modal';
import { EditAfeSourceModal } from './edit-afe-source-modal/edit-afe-source-modal';
import { EditCostDateModal } from './edit-cost-date-modal/edit-cost-date-modal';
import { SuccessToast } from 'components/success-toast/success-toast';

interface SlotsContentProps {
  /** The package ID */
  packageId: number;
  /** The evaluation ID (required for enhanced slot allocations) */
  evalId: number;
  /** The actual Eval 2.0 evaluation ID, used in API calls when slotVolumeSource is 'eval_2_0'. */
  eval2EvaluationId: number;
  /** Current slot volume source setting */
  slotVolumeSource: SlotVolumeSource;
  /** Current AFE source setting */
  slotAfeSource: SlotAfeSource;
  /** Selected DCA scenario ID (null = Legacy) */
  dcaScenarioId?: number | null;
  /** Eval3 calculation ID — enables serving from cached Parquet blob */
  calculationId?: number | null;
  /** Selected DCA scenario name (for warning banner) */
  dcaScenarioName?: string;
  /** Per-slot volume source configurations from edit modal */
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
  /** Callback when a slot's volume source is changed via edit modal */
  onSlotVolumeConfigurationChange: (
    slotId: string,
    volumeSourceId: string,
    volumeSourceType: string,
    volumeSourceHref?: string,
    forecastSource?: string,
    dcaScenario?: string
  ) => void;
  /** Per-slot AFE source configurations from edit modal */
  slotAfeConfigurations: Record<string, SlotAfeSourceConfiguration>;
  /** Callback when a slot's AFE source is changed via edit modal */
  onSlotAfeConfigurationChange: (
    slotId: string,
    afeSourceId: string,
    afeSourceHref?: string,
    costs?: {
      drillingCost?: number;
      completionCost?: number;
      facilitiesCost?: number;
      contingenciesCost?: number;
      otherCost?: number;
    },
    afeWellName?: string,
    costDates?: SlotCostDateConfiguration
  ) => void;
  /** Per-slot cost date configurations from edit modal */
  slotCostDateConfigurations: Record<string, SlotCostDateConfiguration>;
  /** Callback when a slot's cost date is changed via edit modal */
  onSlotCostDateChange: (
    slotId: string,
    dateField: CostDateField,
    dateValue: string
  ) => void;
  /** DPS scheduled dates keyed by slot ID (from blob fetch) */
  scheduledDates?: Record<string, ScheduledSlotDates>;
  /** Per-slot date overrides from the user (layered on top of scheduled dates) */
  slotDateConfigurations?: Record<string, SlotDateOverride>;
  /** Callback when a slot's date (permit/spud/first production) is changed via edit modal */
  onSlotDateChange?: (
    slotId: string,
    dateField: string,
    dateValue: string
  ) => void;
}

/**
 * SlotsContent component for the Enhanced Overview Slots accordion.
 * Displays slot summary, slots by formation, and the enhanced slots table
 * with volume source columns and edit functionality.
 * @return {JSX.Element} The slots content component
 */
export const SlotsContent = ({
  packageId,
  evalId,
  eval2EvaluationId,
  slotVolumeSource,
  slotAfeSource,
  dcaScenarioId,
  calculationId,
  dcaScenarioName,
  slotVolumeConfigurations,
  onSlotVolumeConfigurationChange,
  slotAfeConfigurations,
  onSlotAfeConfigurationChange,
  slotCostDateConfigurations,
  onSlotCostDateChange,
  scheduledDates,
  slotDateConfigurations,
  onSlotDateChange,
}: SlotsContentProps) => {
  // Edit modal state
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null);
  const [editingAfeSlotId, setEditingAfeSlotId] = useState<string | null>(null);
  const [editingCostDate, setEditingCostDate] = useState<{
    slotId: string;
    dateField: CostDateField;
  } | null>(null);
  const [editingSlotDate, setEditingSlotDate] = useState<{
    slotId: string;
    dateField: SlotDateField;
  } | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const slotsColumnOrderConfigs = useEvalExportPresets('slots', 'xlsx');

  // Slot summary counts only (from slot_summary endpoint - does NOT fetch slot_allocations)
  const slotSummaryData = useLoadableAtomValue<SlotSummaryRow[], Error>(
    packageSlotSummaryAtom(packageId)
  );

  // Enhanced slot NRI data (uses paginated POST /enhanced_slot_allocations)
  // This progressively loads pages in the background for fast initial render
  // Also used for SlotsSummary verification counts (eliminates redundant slot_allocations call)
  const {
    rows: enhancedSlots,
    isLoading: isEnhancedSlotNriLoading,
    error: enhancedSlotError,
    hasMore,
    fetchNextPage,
    isFetchingMore,
    undeclinedPdpWellCount,
    setSearch,
    currentSearch,
  } = useEnhancedSlotAllocationsPaginated({
    packageId,
    evalId,
    eval2EvaluationId,
    slotVolumeSource,
    slotAfeSource,
    dcaScenarioId,
    calculationId,
  });

  // Apply user overrides for AFE costs, cost dates, and DPS scheduled dates
  const enhancedSlotsWithOverrides = useMemo(() => {
    return enhancedSlots.map((row) => {
      if (!row.slotId) return row;
      const afeConfig = slotAfeConfigurations[row.slotId];
      const costDateOverrides = slotCostDateConfigurations[row.slotId];
      const scheduled = scheduledDates?.[row.slotId];
      const dateOverrides = slotDateConfigurations?.[row.slotId];

      if (!afeConfig && !costDateOverrides && !scheduled && !dateOverrides)
        return row;

      return {
        ...row,
        // AFE cost overrides
        afeWellName: afeConfig?.afeWellName ?? row.afeWellName,
        slotDrillingCosts: afeConfig?.drillingCost ?? row.slotDrillingCosts,
        slotCompletionCosts:
          afeConfig?.completionCost ?? row.slotCompletionCosts,
        slotFacilitiesCosts:
          afeConfig?.facilitiesCost ?? row.slotFacilitiesCosts,
        slotContingenciesCosts:
          afeConfig?.contingenciesCost ?? row.slotContingenciesCosts,
        slotOtherCosts: afeConfig?.otherCost ?? row.slotOtherCosts,
        // Cost date overrides
        drillingCostsDate:
          costDateOverrides?.drillingCostsDate ?? row.drillingCostsDate,
        completionCostsDate:
          costDateOverrides?.completionCostsDate ?? row.completionCostsDate,
        facilitiesCostsDate:
          costDateOverrides?.facilitiesCostsDate ?? row.facilitiesCostsDate,
        contingenciesCostsDate:
          costDateOverrides?.contingenciesCostsDate ??
          row.contingenciesCostsDate,
        otherCostsDate: costDateOverrides?.otherCostsDate ?? row.otherCostsDate,
        // DPS scheduled dates (base), then user date overrides on top
        permitDate:
          dateOverrides?.permitDate ?? scheduled?.permit_date ?? row.permitDate,
        spudDate:
          dateOverrides?.spudDate ?? scheduled?.spud_date ?? row.spudDate,
        firstProductionDate:
          dateOverrides?.firstProductionDate ??
          scheduled?.first_production_date ??
          row.firstProductionDate,
      };
    });
  }, [
    enhancedSlots,
    slotAfeConfigurations,
    slotCostDateConfigurations,
    scheduledDates,
    slotDateConfigurations,
  ]);

  const hasEnhancedSlotNriError = !!enhancedSlotError;

  // Server-side export hook
  const { exportStatus, startExport } = useSlotsExport({
    packageId,
    eval2EvaluationId,
    calculationId: evalId,
    slotVolumeSource,
    slotAfeSource,
    dcaScenarioId,
    slotVolumeConfigurations,
  });

  // AFE overrides edited since the last eval run. The export reads the last
  // run's blob, so these changes aren't reflected yet — warn before exporting.
  const afeChangedSinceRun = useAtomValue(
    afeChangedSinceLastRunAtom(packageId)
  );
  const [pendingExport, setPendingExport] = useState<ServerExportParams | null>(
    null
  );

  const runServerExport = useCallback(
    ({
      filters,
      sort,
      columnOrder,
      columnPresetName,
      columnOrderExclusive,
      filenameSuffix,
    }: ServerExportParams) => {
      startExport({
        filters,
        sort,
        columnOrder,
        columnPresetName,
        columnOrderExclusive,
        filenameSuffix,
      });
    },
    [startExport]
  );

  // Handle export button click - warns first if AFE overrides aren't yet in the
  // last run, otherwise exports immediately. (AFE is ignored in scenario mode.)
  const handleServerExport = useCallback(
    (params: ServerExportParams) => {
      if (slotAfeSource !== 'scenario' && afeChangedSinceRun.size > 0) {
        setPendingExport(params);
        return;
      }
      runServerExport(params);
    },
    [slotAfeSource, afeChangedSinceRun, runServerExport]
  );

  // SlotSummary state - uses slot_summary for counts, enhanced slots for verification
  const isSlotSummaryLoading =
    slotSummaryData.state === 'loading' || isEnhancedSlotNriLoading;
  const hasSlotSummaryError = slotSummaryData.state === 'hasError';
  const slotSummary =
    slotSummaryData.state === 'hasData' ? slotSummaryData.data : [];

  // Find the slot being edited for modal props
  const editingSlot = editingSlotId
    ? enhancedSlots.find((s) => s.slotId === editingSlotId)
    : null;

  /**
   * Handles the edit button click for a slot's volume source.
   * @param {string} slotId - The ID of the slot to edit
   */
  const handleEditVolumeSource = (slotId: string) => {
    setEditingSlotId(slotId);
  };

  /**
   * Handles saving the volume source selection from the modal.
   * @param {string} volumeSourceId - The selected volume source ID
   * @param {string} volumeSourceType - The type of volume source (pdp_decline or type_curve)
   * @param {string} [volumeSourceHref] - The href for the volume source (for type curves)
   * @param {string} [forecastSource] - The forecast source (Legacy or DCA)
   * @param {string} [dcaScenario] - The DCA scenario name
   */
  const handleSaveVolumeSource = (
    volumeSourceId: string,
    volumeSourceType: string,
    volumeSourceHref?: string,
    forecastSource?: string,
    dcaScenario?: string
  ) => {
    if (editingSlotId) {
      onSlotVolumeConfigurationChange(
        editingSlotId,
        volumeSourceId,
        volumeSourceType,
        volumeSourceHref,
        forecastSource,
        dcaScenario
      );
      setEditingSlotId(null);
    }
  };

  // Get the current volume source ID for the editing slot
  // Only use the override from atom - don't pre-select the smart default
  const currentVolumeSourceId = editingSlotId
    ? slotVolumeConfigurations[editingSlotId]?.volumeSourceId
    : undefined;

  // Find the slot being edited for AFE modal props
  const editingAfeSlot = editingAfeSlotId
    ? enhancedSlots.find((s) => s.slotId === editingAfeSlotId)
    : null;

  const handleEditAfeSource = (slotId: string) => {
    setEditingAfeSlotId(slotId);
  };

  const handleSaveAfeSource = (
    afeId: string,
    afeHref?: string,
    costs?: {
      drillingCost?: number;
      completionCost?: number;
      facilitiesCost?: number;
      contingenciesCost?: number;
      otherCost?: number;
    },
    afeWellName?: string
  ) => {
    if (editingAfeSlotId) {
      onSlotAfeConfigurationChange(
        editingAfeSlotId,
        afeId,
        afeHref,
        costs,
        afeWellName
      );
      setEditingAfeSlotId(null);
    }
  };

  const handleEditCostDate = (slotId: string, dateField: CostDateField) => {
    setEditingCostDate({ slotId, dateField });
  };

  const handleSaveCostDate = (dateValue: string) => {
    if (editingCostDate) {
      onSlotCostDateChange(
        editingCostDate.slotId,
        editingCostDate.dateField,
        dateValue
      );
      setToastMessage(
        `${costDateFieldLabels[editingCostDate.dateField]} updated`
      );
      setEditingCostDate(null);
    }
  };

  const handleEditSlotDate = (slotId: string, dateField: SlotDateField) => {
    setEditingSlotDate({ slotId, dateField });
  };

  const handleSaveSlotDate = (dateValue: string) => {
    if (editingSlotDate && onSlotDateChange) {
      onSlotDateChange(
        editingSlotDate.slotId,
        editingSlotDate.dateField,
        dateValue
      );
      setToastMessage(
        `${slotDateFieldLabels[editingSlotDate.dateField]} updated`
      );
      setEditingSlotDate(null);
    }
  };

  // Routes the bulk-edit payload through the SAME per-slot setters the
  // single-slot edit modals use, so the existing override state — and the
  // configured-cell blue highlight that reads from it — picks up the
  // changes automatically. Only fields present in `patch` are updated;
  // blank fields the user didn't touch are not even iterated, so untouched
  // columns stay unconfigured.
  const COST_DATE_FIELDS: CostDateField[] = [
    'contingenciesCostsDate',
    'facilitiesCostsDate',
    'drillingCostsDate',
    'completionCostsDate',
    'otherCostsDate',
  ];
  const SLOT_DATE_FIELDS: SlotDateField[] = [
    'permitDate',
    'spudDate',
    'firstProductionDate',
  ];

  const handleBulkEditSave = ({
    slotIds,
    patch,
    resolvedAfe,
  }: BulkEditSavePayload) => {
    // Build the AFE side of the call once — same shape the single-slot
    // EditAfeSourceModal sends through handleSaveAfeSource. Costs and well
    // name come from the resolved AvailableAfe so the slot's AFE config
    // ends up complete (afe_header_id + href + costs + well name), not a
    // bare id that would wipe useful display data.
    const afeCosts = resolvedAfe
      ? {
          drillingCost: resolvedAfe.drillingCost,
          completionCost: resolvedAfe.completionCost,
          facilitiesCost: resolvedAfe.facilitiesCost,
          contingenciesCost: resolvedAfe.contingenciesCost,
          otherCost: resolvedAfe.otherCost,
        }
      : undefined;
    const afeHref = resolvedAfe?.href ?? undefined;
    const afeWellName = resolvedAfe?.well?.name ?? undefined;

    slotIds.forEach((slotId) => {
      if (patch.afeSourceId !== undefined && patch.afeSourceId !== '') {
        onSlotAfeConfigurationChange(
          slotId,
          patch.afeSourceId,
          afeHref,
          afeCosts,
          afeWellName
        );
      }
      COST_DATE_FIELDS.forEach((field) => {
        const value = patch[field];
        if (value !== undefined && value !== '') {
          onSlotCostDateChange(slotId, field, value);
        }
      });
      if (onSlotDateChange) {
        SLOT_DATE_FIELDS.forEach((field) => {
          const value = patch[field];
          if (value !== undefined && value !== '') {
            onSlotDateChange(slotId, field, value);
          }
        });
      }
    });
  };

  // Determine if edit is disabled (eval 2.0 mode)
  const isEval2Mode = slotVolumeSource === 'eval_2_0';
  const isScenarioMode = slotAfeSource === 'scenario';

  const filteredColumns = isScenarioMode
    ? ENHANCED_OVERVIEW_SLOT_COLUMNS.filter((c) => !AFE_COLUMNS.has(c))
    : ENHANCED_OVERVIEW_SLOT_COLUMNS;

  // Hide override indicators in eval 2.0 mode (data is preserved in state)
  const overriddenSlotIds = isEval2Mode
    ? []
    : Object.keys(slotVolumeConfigurations);

  const overriddenVolumeSourceIds = isEval2Mode
    ? {}
    : Object.fromEntries(
        Object.entries(slotVolumeConfigurations).map(([slotId, config]) => [
          slotId,
          config.forecastSource === 'DCA'
            ? `Insights_${config.volumeSourceId}`
            : config.volumeSourceId,
        ])
      );

  const overriddenDcaScenarios = isEval2Mode
    ? {}
    : Object.fromEntries(
        Object.entries(slotVolumeConfigurations)
          .filter(([, config]) => config.dcaScenario)
          .map(([slotId, config]) => [slotId, config.dcaScenario!])
      );

  const overriddenVolumeSourceHrefs = isEval2Mode
    ? {}
    : Object.fromEntries(
        Object.entries(slotVolumeConfigurations)
          .filter(([, config]) => config.volumeSourceHref)
          .map(([slotId, config]) => [slotId, config.volumeSourceHref!])
      );

  const editingCostDateSlot = editingCostDate
    ? enhancedSlotsWithOverrides.find(
        (s) => s.slotId === editingCostDate.slotId
      )
    : null;

  const currentCostDateValue = editingCostDate
    ? editingCostDateSlot?.[editingCostDate.dateField]
    : undefined;

  const editingSlotDateSlot = editingSlotDate
    ? enhancedSlotsWithOverrides.find(
        (s) => s.slotId === editingSlotDate.slotId
      )
    : null;

  const currentSlotDateValue = editingSlotDate
    ? editingSlotDateSlot?.[editingSlotDate.dateField]
    : undefined;

  const slotDateFieldLabels: Record<SlotDateField, string> = {
    permitDate: 'Permit Date',
    spudDate: 'Spud Date',
    firstProductionDate: 'First Production Date',
  };

  const costDateFieldLabels: Record<CostDateField, string> = {
    contingenciesCostsDate: 'Contingencies Costs Date',
    facilitiesCostsDate: 'Facilities Costs Date',
    drillingCostsDate: 'Drilling Costs Date',
    completionCostsDate: 'Completion Costs Date',
    otherCostsDate: 'Other Costs Date',
  };

  return (
    <div>
      {hasSlotSummaryError && (
        <InlineNotification
          kind="error"
          title={slotSummaryData.error.message}
          lowContrast
        />
      )}
      {hasEnhancedSlotNriError && (
        <InlineNotification
          kind="error"
          title={enhancedSlotError ?? 'Error loading enhanced slots'}
          lowContrast
        />
      )}
      {exportStatus.status === 'failed' && (
        <InlineNotification
          kind="error"
          title={exportStatus.error ?? 'Export failed'}
          lowContrast
        />
      )}
      {exportStatus.status === 'processing' && (
        <InlineNotification
          kind="info"
          title="Export in progress..."
          subtitle="You will receive a download when complete."
          lowContrast
        />
      )}
      <SlotsSummary
        slotSummary={slotSummary}
        slots={enhancedSlotsWithOverrides}
        isLoading={isSlotSummaryLoading}
        showHighlight={false}
        tableId="package-eval-v3-slot-summary-table"
      />
      {undeclinedPdpWellCount > 0 && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={`${formatNumber({
            value: undeclinedPdpWellCount,
            minPrecision: 0,
            includeCommas: true,
          })} PDP wells have not been declined with "${
            dcaScenarioName ?? 'Legacy'
          }"`}
          subtitle="Decline these wells with the selected scenario or contact the Reservoir Engineering team before running calculations."
        />
      )}
      <FullNRITable
        tractNRIRows={enhancedSlotsWithOverrides}
        isLoading={isEnhancedSlotNriLoading}
        tableId="package-eval-v3-enhanced-slot-table"
        tractNRIColumns={filteredColumns}
        packageId={packageId}
        subtractNRIColumns={[]}
        volumeSourceConfig={{
          onEdit: handleEditVolumeSource,
          isEditDisabled: isEval2Mode,
          overriddenSlotIds,
          overriddenVolumeSourceIds,
          overriddenVolumeSourceHrefs,
          overriddenDcaScenarios,
        }}
        afeSourceConfig={{
          onEditAfeSource: handleEditAfeSource,
          isEditDisabled: isScenarioMode,
          overriddenAfeSlotIds: isScenarioMode
            ? []
            : Object.keys(slotAfeConfigurations),
          changedSinceRunAfeSlotIds: isScenarioMode
            ? []
            : Array.from(afeChangedSinceRun),
          overriddenAfeSourceIds: Object.fromEntries(
            Object.entries(slotAfeConfigurations).map(([slotId, config]) => [
              slotId,
              config.afeSourceId,
            ])
          ),
          overriddenAfeSourceHrefs: Object.fromEntries(
            Object.entries(slotAfeConfigurations)
              .filter(([, config]) => config.afeSourceHref)
              .map(([slotId, config]) => [slotId, config.afeSourceHref!])
          ),
          onEditCostDate: handleEditCostDate,
          overriddenCostDates: slotCostDateConfigurations,
          onEditSlotDate: onSlotDateChange ? handleEditSlotDate : undefined,
          overriddenSlotDates: slotDateConfigurations,
        }}
        onServerExport={handleServerExport}
        isServerExporting={exportStatus.status === 'processing'}
        exportPresets={slotsColumnOrderConfigs}
        hasMore={hasMore}
        onLoadMore={fetchNextPage}
        isFetchingMore={isFetchingMore}
        onSearchChange={setSearch}
        searchValue={currentSearch}
        onBulkEditSave={handleBulkEditSave}
        enableSlotSelection
      />
      <EditVolumeSourceModal
        isOpen={editingSlotId !== null}
        onClose={() => setEditingSlotId(null)}
        onSave={handleSaveVolumeSource}
        slotId={editingSlotId ?? ''}
        packageId={packageId}
        typeCurveAreaName={editingSlot?.typeCurveAreaName}
        wellName={editingSlot?.wellName}
        currentVolumeSourceId={currentVolumeSourceId}
      />
      <EditAfeSourceModal
        isOpen={editingAfeSlotId !== null}
        onClose={() => setEditingAfeSlotId(null)}
        onSave={handleSaveAfeSource}
        slotId={editingAfeSlotId ?? ''}
        wellName={editingAfeSlot?.wellName}
        currentAfeSourceId={
          editingAfeSlotId
            ? (slotAfeConfigurations[editingAfeSlotId]?.afeSourceId ??
              editingAfeSlot?.afeSourceId)
            : undefined
        }
      />
      <EditCostDateModal
        isOpen={editingCostDate !== null}
        onClose={() => setEditingCostDate(null)}
        onSave={handleSaveCostDate}
        slotId={editingCostDate?.slotId ?? ''}
        wellName={editingCostDateSlot?.wellName}
        dateFieldLabel={
          editingCostDate ? costDateFieldLabels[editingCostDate.dateField] : ''
        }
        currentDate={currentCostDateValue}
      />
      <EditCostDateModal
        isOpen={editingSlotDate !== null}
        onClose={() => setEditingSlotDate(null)}
        onSave={handleSaveSlotDate}
        slotId={editingSlotDate?.slotId ?? ''}
        wellName={editingSlotDateSlot?.wellName}
        dateFieldLabel={
          editingSlotDate ? slotDateFieldLabels[editingSlotDate.dateField] : ''
        }
        currentDate={currentSlotDateValue}
      />
      <Modal
        open={pendingExport !== null}
        modalHeading="AFE changes not yet in results"
        primaryButtonText="Export anyway"
        secondaryButtonText="Cancel"
        onRequestClose={() => setPendingExport(null)}
        onRequestSubmit={() => {
          if (pendingExport) runServerExport(pendingExport);
          setPendingExport(null);
        }}
      >
        <p>
          {afeChangedSinceRun.size} slot
          {afeChangedSinceRun.size === 1 ? '' : 's'} ha
          {afeChangedSinceRun.size === 1 ? 's' : 've'} AFE overrides that
          changed since the last eval run. This export reflects the last run —
          re-run the eval to include these changes.
        </p>
      </Modal>
      {toastMessage && (
        <SuccessToast
          message={toastMessage}
          onClose={() => {
            setToastMessage(null);
            return false;
          }}
        />
      )}
    </div>
  );
};
